import { useState } from "react";
import { Play } from "lucide-react";
import { BACKEND_URL } from "../lib/api";

// Extract the YouTube video ID from a URL
const parseYouTubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : null;
};

// Extract the Vimeo video ID from a URL
const parseVimeoId = (url) => {
  if (!url) return null;
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
};

const buildUploadUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
};

const buildThumbnail = (video) => {
  if (video.thumbnail) return video.thumbnail;
  if (video.type === "youtube") {
    const id = parseYouTubeId(video.url);
    if (id) return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
  }
  return null;
};

export default function VideoEmbed({ video, className = "" }) {
  const [play, setPlay] = useState(false);

  if (!video || !video.url) return null;

  if (video.type === "upload") {
    const src = buildUploadUrl(video.url);
    return (
      <div className={`relative w-full aspect-video bg-black rounded-2xl overflow-hidden ${className}`} data-testid={`video-embed-${video.video_id}`}>
        <video
          src={src}
          controls
          preload="metadata"
          poster={video.thumbnail || undefined}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  const embedSrc = (() => {
    if (video.type === "youtube") {
      const id = parseYouTubeId(video.url);
      return id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` : null;
    }
    if (video.type === "vimeo") {
      const id = parseVimeoId(video.url);
      return id ? `https://player.vimeo.com/video/${id}?autoplay=1` : null;
    }
    return null;
  })();

  const thumb = buildThumbnail(video);

  return (
    <div className={`relative w-full aspect-video bg-black rounded-2xl overflow-hidden group ${className}`} data-testid={`video-embed-${video.video_id}`}>
      {play && embedSrc ? (
        <iframe
          src={embedSrc}
          title={video.title || "Vidéo"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
          className="absolute inset-0 w-full h-full"
        />
      ) : (
        <button
          type="button"
          onClick={() => setPlay(true)}
          className="absolute inset-0 flex items-center justify-center"
          aria-label={`Lancer la lecture : ${video.title || "vidéo"}`}
          data-testid={`play-video-${video.video_id}`}
        >
          {thumb ? (
            <img src={thumb} alt={video.title || ""} className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-[#0052CC] to-[#001A5C]" />
          )}
          <span className="relative z-10 flex items-center justify-center w-20 h-20 rounded-full bg-white/95 shadow-2xl group-hover:scale-110 transition">
            <Play className="w-9 h-9 text-[#E3000F] fill-current ml-1" />
          </span>
        </button>
      )}
    </div>
  );
}
